import { usePunditAPI } from "@circle-react/hooks/usePunditAPI";
import type { ReactNode } from "react";
import { createContext, useContext, useMemo } from "react";

import { communitySignupUrl } from "@/react/helpers/communityHelpers";

import { useExperimentationFlags } from "./useExperimentationFlags";

type EmptyObject = Record<string, undefined>;

const emptyPunditUser: EmptyObject = {};

const usePunditUserContextState = () => {
  const { data, refetch, isPending } = usePunditAPI();
  const resData = data || emptyPunditUser;
  const currentUser = resData?.current_user;
  const currentCommunity = resData?.current_community;
  const isCaiSufEnabled = !!resData?.cai_suf_enabled;
  const isEligibleForNewOnboardingFlow =
    !!resData?.eligible_for_new_onboarding_flow;
  const currentCommunitySettings = resData?.current_community_settings;
  const onboardingFlags = resData?.onboarding_flags;
  const currentCommunityMember = resData?.current_community_member;
  const shouldDisplayCommunitySwitcher = !!resData?.display_community_switcher;
  const isViewOnlyMasquerading = !!resData?.view_only_masquerading;
  const masqueradingEntity = resData?.masquerading_entity;
  const circuitBreakers = resData?.circuit_breakers;

  const {
    flags: experimentationFlags,
    experiments: experimentationExperiments,
    isLoading: isExperimentationFlagsLoading,
  } = useExperimentationFlags({
    communityId: currentCommunity?.id,
    currentUserId: currentUser?.id,
  });

  const memoizedExperimentationFlags = useMemo(
    () => ({
      flags: experimentationFlags,
      experiments: experimentationExperiments,
      isLoading: isExperimentationFlagsLoading,
    }),
    [
      experimentationFlags,
      experimentationExperiments,
      isExperimentationFlagsLoading,
    ],
  );

  return useMemo(
    () => ({
      refetch,
      isLoading: isPending,
      isApiLoading: isPending, // TODO: Redundant flag, remove it
      apiData: data,
      data: resData,
      currentUser,
      currentCommunity,
      isCaiSufEnabled,
      isEligibleForNewOnboardingFlow,
      currentCommunitySettings,
      currentCommunityMember,
      onboardingFlags,
      displayCommunitySwitcher: shouldDisplayCommunitySwitcher,
      isViewOnlyMasquerading,
      masqueradingEntity,
      experimentation: memoizedExperimentationFlags,
      circuitBreakers,
    }),
    [
      refetch,
      isPending,
      data,
      resData,
      currentUser,
      currentCommunity,
      isCaiSufEnabled,
      isEligibleForNewOnboardingFlow,
      currentCommunitySettings,
      currentCommunityMember,
      onboardingFlags,
      shouldDisplayCommunitySwitcher,
      isViewOnlyMasquerading,
      masqueradingEntity,
      memoizedExperimentationFlags,
      circuitBreakers,
    ],
  );
};

export type PunditUserContext = ReturnType<typeof usePunditUserContextState>;

const PunditUserReactContext = createContext<PunditUserContext | null>(null);
PunditUserReactContext.displayName = "PunditUserContext";

export const PunditUserContextProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const value = usePunditUserContextState();

  return (
    <PunditUserReactContext.Provider value={value}>
      {children}
    </PunditUserReactContext.Provider>
  );
};

export const usePunditUserContext = (): PunditUserContext => {
  const value = useContext(PunditUserReactContext);

  if (!value) {
    throw new Error(
      "usePunditUserContext must be used within PunditUserContextProvider",
    );
  }

  return value;
};

export const useCurrentCommunityMember = () =>
  usePunditUserContext().currentCommunityMember;

export const useCurrentUser = () => usePunditUserContext().currentUser;

export const useIfSignupEnabled = () => {
  const { currentCommunity, currentUser } = usePunditUserContext();

  return (callbackFn: (data: { communitySignupUrl: string }) => ReactNode) => {
    if (currentUser) return null;
    const _communitySignupUrl = communitySignupUrl(currentCommunity);

    if (_communitySignupUrl) {
      return callbackFn({ communitySignupUrl: _communitySignupUrl });
    }
    return null;
  };
};

/**
 * Returns true if the current user is already a member of the community, or if
 * they can become one if the current community has public signups enabled.
 * Returns false otherwise.
 */
export const useIsOrCanBeCommunityMember = () => {
  const { currentCommunityMember, currentCommunity } = usePunditUserContext();

  return Boolean(
    currentCommunityMember ||
    currentCommunity?.allow_signups_to_public_community,
  );
};

export const useIsCommunityAdmin = () =>
  Boolean(useCurrentCommunityMember()?.is_admin);

export const useIsAdminOrModerator = () => {
  const member = useCurrentCommunityMember();
  return Boolean(member?.is_admin || member?.is_moderator);
};

export const useIsCaiSufEnabled = () => usePunditUserContext().isCaiSufEnabled;

export const useIsEligibleForNewOnboardingFlow = () =>
  usePunditUserContext().isEligibleForNewOnboardingFlow;
